import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api, fmtMoney, formatErr } from "@/lib/api";
import Modal, { inputCls, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { TableCard, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { Plus, ClipboardList, PackageCheck, Wallet, Clock, Eye, CheckCheck, XCircle, Trash2, Truck, Download } from "lucide-react";
import { downloadPurchaseOrder } from "@/lib/pdf";
import { toast } from "sonner";

const PAGE = 20;
const todayInput = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
// Money-affecting keys to refresh after a PO mutation (advances hit suppliers/payables/cash).
const KEYS = ["purchase-orders", "purchases", "purchase-invoices", "products", "suppliers", "supplier",
  "payables", "dashboard", "financial-position", "pnl-series", "movements", "notifications",
  "report-stock", "report-cash", "report-pnl", "supplier-payments", "product-analytics"];

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-50 text-agri-amber-dark" },
  received: { label: "Received", cls: "bg-blue-50 text-blue-700" },
  matured: { label: "Matured", cls: "bg-green-50 text-agri-forest" },
  cancelled: { label: "Cancelled", cls: "bg-black/5 text-agri-ink-2" },
};
function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

const FILTERS = [["all", "All"], ["pending", "Pending"], ["received", "Received"], ["matured", "Matured"], ["cancelled", "Cancelled"]];
const emptyLine = () => ({ product_id: "", quantity: 1 });

export default function PurchaseOrders({ suppliers = [], products = [] }) {
  const qc = useQueryClient();
  const refresh = () => KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const dt = (v) => (v ? new Date(v).toLocaleDateString(undefined, { timeZone: "Asia/Karachi" }) : "—");
  const prodCost = (id) => products.find((p) => p.id === id)?.purchase_price || 0;

  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [filter]);

  const { data } = useQuery({
    queryKey: ["purchase-orders", filter, page],
    queryFn: () => api.get("/purchase-orders", { params: { status: filter, skip: page * PAGE, limit: PAGE } })
      .then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] ?? r.data.length) })),
    placeholderData: keepPreviousData,
  });
  // KPIs come from the pending+received set (open commitments), fetched once regardless of filter.
  const { data: openPOs = [] } = useQuery({
    queryKey: ["purchase-orders", "open"],
    queryFn: async () => {
      const [p, r] = await Promise.all([
        api.get("/purchase-orders", { params: { status: "pending", limit: 500 } }).then((x) => x.data),
        api.get("/purchase-orders", { params: { status: "received", limit: 500 } }).then((x) => x.data),
      ]);
      return [...p, ...r];
    },
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const openCount = openPOs.length;
  const awaiting = openPOs.filter((o) => o.status === "received").length;
  const committed = openPOs.reduce((s, o) => s + (o.total || 0), 0);
  const advances = openPOs.reduce((s, o) => s + (o.advance_paid || 0), 0);

  /* ---- create ---- */
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", advance_paid: 0, invoice_no: "", date: "", notes: "", lines: [emptyLine()] });
  const openNew = () => { setForm({ supplier_id: "", advance_paid: 0, invoice_no: "", date: todayInput(), notes: "", lines: [emptyLine()] }); setOpen(true); };
  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, k) => k === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, k) => k !== i) }));
  // Only the selected supplier's products are orderable (products link to a supplier by name).
  const selectedSupplier = suppliers.find((s) => s.id === form.supplier_id);
  const supplierProducts = selectedSupplier ? products.filter((p) => (p.supplier_name || "") === selectedSupplier.name) : [];
  const orderTotal = form.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * prodCost(l.product_id), 0);
  const advNum = Math.min(parseFloat(form.advance_paid) || 0, orderTotal);
  const balance = Math.max(0, orderTotal - advNum);

  const create = useMutation({
    mutationFn: () => api.post("/purchase-orders", {
      supplier_id: form.supplier_id,
      advance_paid: advNum,
      invoice_no: form.invoice_no,
      notes: form.notes,
      date: form.date || undefined,
      lines: form.lines.filter((l) => l.product_id && (parseFloat(l.quantity) || 0) > 0)
        .map((l) => ({ product_id: l.product_id, quantity: parseFloat(l.quantity) })),
    }),
    onSuccess: () => { toast.success("Purchase order placed"); refresh(); setOpen(false); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });
  const canCreate = form.supplier_id && form.lines.some((l) => l.product_id && (parseFloat(l.quantity) || 0) > 0);

  /* ---- view / receive / mature ---- */
  const [view, setView] = useState(null);            // the PO being viewed
  const [recv, setRecv] = useState([]);              // [{received, received_qty}] aligned to view.lines
  const openView = (po) => {
    setView(po);
    setRecv(po.lines.map((l) => ({ received: !!l.received, received_qty: l.received_qty ?? l.quantity })));
  };
  const setRecvLine = (i, patch) => setRecv((r) => r.map((x, k) => k === i ? { ...x, ...patch } : x));

  const receive = useMutation({
    mutationFn: () => api.post(`/purchase-orders/${view.id}/receive`, {
      lines: recv.map((x) => ({ received: !!x.received, received_qty: x.received ? (parseFloat(x.received_qty) || 0) : null })),
    }),
    onSuccess: (r) => {
      refresh();
      setView(r.data);
      if (r.data.status === "received") {
        toast.success("All items received");
        if (window.confirm(`All items for ${r.data.po_no} are received. Mature this invoice into stock now?`)) mature.mutate(r.data.id);
      } else {
        toast.success("Receipt updated");
      }
    },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });
  const mature = useMutation({
    mutationFn: (id) => api.post(`/purchase-orders/${id}/mature`),
    onSuccess: () => { toast.success("Invoice matured — stock added"); refresh(); setView(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });
  const cancel = useMutation({
    mutationFn: (id) => api.post(`/purchase-orders/${id}/cancel`),
    onSuccess: () => { toast.success("Order cancelled — any advance refunded"); refresh(); setView(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/purchase-orders/${id}`),
    onSuccess: () => { toast.success("Order deleted"); refresh(); setView(null); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const recvTotal = view ? view.lines.reduce((s, l, i) => s + (recv[i]?.received ? (parseFloat(recv[i]?.received_qty) || 0) : 0) * (l.unit_cost || 0), 0) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-agri-ink-2 max-w-2xl">
          Order stock from a supplier <span className="font-medium text-agri-ink">before it arrives</span>. Pay an advance now if you like — it's held as supplier credit.
          When the goods come in, mark each line received and <span className="font-medium text-agri-ink">mature</span> the invoice to add them to stock.
        </p>
        <button onClick={openNew} className={btnAccent + " flex items-center gap-2 shrink-0"}><Plus size={16} /> New Purchase Order</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Open Orders" value={openCount} icon={ClipboardList} hint="pending + received" />
        <Stat label="Awaiting Maturity" value={awaiting} icon={PackageCheck} tone={awaiting > 0 ? "amber" : "forest"} hint="all items received" />
        <Stat label="Committed Value" value={fmtMoney(committed)} icon={Clock} hint="on open orders" />
        <Stat label="Advances Paid" value={fmtMoney(advances)} icon={Wallet} tone="amber" hint="held with suppliers" />
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink-2 hover:text-agri-ink"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>PO #</th><th className={thCls}>Date</th><th className={thCls}>Supplier</th>
            <th className={thNumCls}>Items</th><th className={thNumCls}>Total</th><th className={thNumCls}>Advance</th>
            <th className={thNumCls}>Balance</th><th className={thCls}>Status</th><th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={9} icon={ClipboardList}>No purchase orders here.</EmptyRow>}
          {rows.map((po) => {
            const bal = Math.max(0, (po.total || 0) - (po.advance_paid || 0));
            return (
              <tr key={po.id} className={`${rowCls} cursor-pointer`} onClick={() => openView(po)}>
                <td data-label="PO #" className={`${tdCls} font-medium`}>{po.po_no}</td>
                <td data-label="Date" className={`${tdCls} text-agri-ink-2 whitespace-nowrap`}>{dt(po.date || po.created_at)}</td>
                <td data-label="Supplier" className={tdCls}>{po.supplier_name}</td>
                <td data-label="Items" className={tdNumCls}>{po.lines.length}</td>
                <td data-label="Total" className={`${tdNumCls} font-semibold`}>{fmtMoney(po.total)}</td>
                <td data-label="Advance" className={tdNumCls}>{fmtMoney(po.advance_paid)}</td>
                <td data-label="Balance" className={`${tdNumCls} ${bal > 0 ? "text-agri-ink" : "text-agri-ink-2"}`}>{po.status === "matured" || po.status === "cancelled" ? "—" : fmtMoney(bal)}</td>
                <td data-label="Status" className={tdCls}><StatusPill status={po.status} /></td>
                <td className={`${tdCls} text-right rt-actions whitespace-nowrap`}>
                  <button onClick={(e) => { e.stopPropagation(); openView(po); }} title="View"
                    className="p-1.5 rounded hover:bg-agri-forest/5 text-agri-ink-2 hover:text-agri-forest align-middle"><Eye size={15} /></button>
                  <button onClick={(e) => { e.stopPropagation(); downloadPurchaseOrder(po); }} title="Download PO"
                    className="p-1.5 rounded hover:bg-agri-amber/10 text-agri-ink-2 hover:text-agri-amber-dark align-middle ml-0.5"><Download size={15} /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableCard>
      <Pagination page={page} pageSize={PAGE} total={total} onPage={setPage} />

      {/* Create PO */}
      <Modal open={open} onClose={() => setOpen(false)} title="New Purchase Order" size="xl">
        <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Supplier</label>
              <select required value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value, lines: [emptyLine()] })} className={inputCls}>
                <option value="">Select…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
            <div><label className={labelCls}>Order date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          </div>

          <div>
            <label className={labelCls}>Items ordered</label>
            <div className="space-y-2">
              {form.lines.map((l, i) => {
                const cost = prodCost(l.product_id);
                return (
                  <div key={i} className="flex items-center gap-2 border border-[rgba(28,28,30,0.08)] rounded-lg p-2">
                    <select value={l.product_id} disabled={!form.supplier_id} onChange={(e) => setLine(i, { product_id: e.target.value })} className={inputCls + " flex-1"}>
                      <option value="">{form.supplier_id ? (supplierProducts.length ? "Select product…" : "No products for this supplier") : "Select a supplier first"}</option>
                      {supplierProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <div className="w-24"><NumberInput value={l.quantity} emptyValue={1} onChange={(n) => setLine(i, { quantity: n })} /></div>
                    <div className="w-28 text-right text-sm tabular-nums text-agri-ink-2">{l.product_id ? fmtMoney((parseFloat(l.quantity) || 0) * cost) : "—"}</div>
                    <button type="button" onClick={() => rmLine(i)} disabled={form.lines.length === 1}
                      className="p-1.5 rounded text-agri-ink-2 hover:text-agri-danger hover:bg-red-50 disabled:opacity-30"><Trash2 size={15} /></button>
                  </div>
                );
              })}
            </div>
            <button type="button" onClick={addLine} disabled={!form.supplier_id} className="mt-2 text-xs font-medium text-agri-forest hover:underline flex items-center gap-1 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"><Plus size={13} /> Add item</button>
            <p className="text-[11px] text-agri-ink-2 mt-1">Only this supplier's products are listed. Cost is the product's current price (agreed price for this order); stock is NOT added until the order matures. To change which supplier a product belongs to, edit it in Products.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Advance paid now</label>
              <NumberInput value={form.advance_paid} max={orderTotal} onChange={(n) => setForm({ ...form, advance_paid: n })} />
              <p className="text-[11px] text-agri-ink-2 mt-1">Cash paid to the supplier now — held as an advance, applied when matured.</p></div>
            <div><label className={labelCls}>Supplier invoice # (optional)</label>
              <input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Notes</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>

          <div className="flex flex-wrap justify-between gap-3 text-sm border-t border-[rgba(28,28,30,0.06)] pt-3">
            <span className="text-agri-ink-2">Order total <span className="font-bold text-agri-ink tabular-nums ml-1">{fmtMoney(orderTotal)}</span></span>
            <span className="text-agri-ink-2">Advance <span className="font-semibold text-agri-forest tabular-nums ml-1">{fmtMoney(advNum)}</span></span>
            <span className="text-agri-ink-2">Balance on delivery <span className="font-semibold text-agri-danger tabular-nums ml-1">{fmtMoney(balance)}</span></span>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
            <button className={btnPrimary} disabled={!canCreate || create.isPending}>{create.isPending ? "Placing…" : "Place order"}</button>
          </div>
        </form>
      </Modal>

      {/* View / receive / mature */}
      <Modal open={!!view} onClose={() => setView(null)} size="xl"
        title={view ? `${view.po_no} · ${view.supplier_name}` : ""}>
        {view && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-agri-ink-2">
              <StatusPill status={view.status} />
              <span>Ordered {dt(view.date || view.created_at)}</span>
              {view.invoice_no && <span>· Inv {view.invoice_no}</span>}
              {view.notes && <span>· {view.notes}</span>}
            </div>

            <div className="rounded-lg border border-[rgba(28,28,30,0.08)] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-agri-cream/70">
                  <tr>
                    <th className="text-left px-3 py-2 label-cap">Product</th>
                    <th className="text-right px-3 py-2 label-cap">Ordered</th>
                    <th className="text-right px-3 py-2 label-cap">Cost</th>
                    {view.status !== "matured" && view.status !== "cancelled" && <th className="text-center px-3 py-2 label-cap">Received?</th>}
                    {view.status !== "matured" && view.status !== "cancelled" && <th className="text-right px-3 py-2 label-cap">Qty recd</th>}
                  </tr>
                </thead>
                <tbody>
                  {view.lines.map((l, i) => {
                    const editable = view.status === "pending" || view.status === "received";
                    return (
                      <tr key={i} className="border-t border-[rgba(28,28,30,0.05)]">
                        <td className="px-3 py-2 font-medium">{l.product_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-agri-ink-2">{fmtMoney(l.unit_cost)}</td>
                        {editable && (
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={!!recv[i]?.received}
                              onChange={(e) => setRecvLine(i, { received: e.target.checked })}
                              className="h-4 w-4 accent-agri-forest align-middle" />
                          </td>
                        )}
                        {editable && (
                          <td className="px-3 py-2 text-right">
                            <div className="w-24 ml-auto">
                              <NumberInput value={recv[i]?.received_qty ?? l.quantity} disabled={!recv[i]?.received}
                                onChange={(n) => setRecvLine(i, { received_qty: n })} />
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap justify-between gap-3 text-sm">
              <span className="text-agri-ink-2">Order total <span className="font-semibold text-agri-ink tabular-nums ml-1">{fmtMoney(view.total)}</span></span>
              <span className="text-agri-ink-2">Advance paid <span className="font-semibold text-agri-forest tabular-nums ml-1">{fmtMoney(view.advance_paid)}</span></span>
              {(view.status === "pending" || view.status === "received") &&
                <span className="text-agri-ink-2">Receiving value <span className="font-semibold text-agri-ink tabular-nums ml-1">{fmtMoney(recvTotal)}</span></span>}
            </div>

            {(view.status === "pending" || view.status === "received") && (
              <p className="text-[11px] text-agri-ink-2">
                Maturing adds the received quantities to stock and puts the balance
                (<span className="tabular-nums">{fmtMoney(Math.max(0, recvTotal - (view.advance_paid || 0)))}</span>) onto the supplier's payable.
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2 border-t border-[rgba(28,28,30,0.06)] pt-3">
              {view.status === "matured" && <span className="text-xs text-agri-forest self-center mr-auto">Matured into {view.matured_purchase_ids?.length || 0} purchase line(s).</span>}
              <button onClick={() => downloadPurchaseOrder(view)} className={btnOutline + " flex items-center gap-2"}><Download size={15} /> Download PO</button>
              {(view.status === "pending" || view.status === "received") && (
                <button onClick={() => { if (window.confirm(`Cancel ${view.po_no}? Any advance is refunded.`)) cancel.mutate(view.id); }}
                  className={btnOutline + " flex items-center gap-2 text-agri-danger"}><XCircle size={15} /> Cancel order</button>
              )}
              {(view.status === "cancelled") && (
                <button onClick={() => { if (window.confirm(`Permanently delete ${view.po_no}?`)) del.mutate(view.id); }}
                  className={btnOutline + " flex items-center gap-2 text-agri-danger"}><Trash2 size={15} /> Delete</button>
              )}
              {(view.status === "pending" || view.status === "received") && (
                <button onClick={() => receive.mutate()} disabled={receive.isPending} className={btnOutline + " flex items-center gap-2"}>
                  <Truck size={15} /> {receive.isPending ? "Saving…" : "Save receipt"}
                </button>
              )}
              {(view.status === "received") && (
                <button onClick={() => { if (window.confirm(`Mature ${view.po_no} into stock now?`)) mature.mutate(view.id); }}
                  disabled={mature.isPending} className={btnPrimary + " flex items-center gap-2"}>
                  <CheckCheck size={15} /> {mature.isPending ? "Maturing…" : "Mature invoice"}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
